import React, { CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

/**
 * Props do componente MessageContent
 */
export interface MessageContentProps {
  /** Conteúdo da mensagem (texto ou Markdown) */
  content: string;
  /** Habilitar renderização de Markdown */
  enableMarkdown?: boolean;
  /** Estilos customizados para o container */
  customStyles?: CSSProperties;
  /** Se é mensagem do usuário (afeta cor dos links) */
  isUser?: boolean;
}

/**
 * Componente para renderizar conteúdo de mensagem com suporte a Markdown
 */
export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  enableMarkdown = true,
  customStyles,
  isUser = false,
}) => {
  // Se Markdown estiver desabilitado, renderiza como texto simples
  if (!enableMarkdown) {
    return <div style={customStyles}>{content}</div>;
  }

  // Processa conteúdo para garantir quebras de linha corretas

  // Primeiro, converte \\n (string literal escapada) em \n (quebra de linha real)
  // Isso acontece quando o backend envia "texto\\ntexto" ao invés de "texto\ntexto"
  let processedContent = content.replace(/\\n/g, '\n');

  // Depois, converte \n simples em dois espaços + \n (sintaxe Markdown para line break)
  // Mas preserva \n\n (parágrafos)
  processedContent = processedContent.replace(/\n(?!\n)/g, '  \n');

  // Estilos para elementos Markdown
  const markdownStyles = {
    container: {
      ...customStyles,
    } as CSSProperties,

    paragraph: {
      margin: '0.5em 0',
    } as CSSProperties,

    code: {
      backgroundColor: isUser ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.9em',
      fontFamily: 'monospace',
    } as CSSProperties,

    codeBlock: {
      backgroundColor: isUser ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.08)',
      padding: '0.75em',
      borderRadius: '6px',
      fontSize: '0.85em',
      fontFamily: 'monospace',
      overflowX: 'auto',
      margin: '0.5em 0',
    } as CSSProperties,

    link: {
      color: isUser ? '#e0e7ff' : '#4f46e5',
      textDecoration: 'underline',
    } as CSSProperties,

    list: {
      margin: '0.5em 0',
      paddingLeft: '1.5em',
    } as CSSProperties,

    listItem: {
      margin: '0.25em 0',
    } as CSSProperties,

    blockquote: {
      borderLeft: `3px solid ${isUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
      paddingLeft: '1em',
      margin: '0.5em 0',
      fontStyle: 'italic',
    } as CSSProperties,

    heading: {
      margin: '0.75em 0 0.5em 0',
      fontWeight: 600,
    } as CSSProperties,
  };

  return (
    <div style={markdownStyles.container}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          // Parágrafos
          p: ({ node, ...props }) => (
            <p style={markdownStyles.paragraph} {...props} />
          ),

          // Código inline
          code: ({ node, inline, ...props }: any) => {
            if (inline) {
              return <code style={markdownStyles.code} {...props} />;
            }
            return (
              <pre style={markdownStyles.codeBlock}>
                <code {...props} />
              </pre>
            );
          },

          // Links
          a: ({ node, ...props }) => (
            <a
              style={markdownStyles.link}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),

          // Listas
          ul: ({ node, ...props }) => (
            <ul style={markdownStyles.list} {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol style={markdownStyles.list} {...props} />
          ),
          li: ({ node, ...props }) => (
            <li style={markdownStyles.listItem} {...props} />
          ),

          // Citações
          blockquote: ({ node, ...props }) => (
            <blockquote style={markdownStyles.blockquote} {...props} />
          ),

          // Headings
          h1: ({ node, ...props }) => (
            <h1 style={{ ...markdownStyles.heading, fontSize: '1.5em' }} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 style={{ ...markdownStyles.heading, fontSize: '1.3em' }} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 style={{ ...markdownStyles.heading, fontSize: '1.1em' }} {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 style={{ ...markdownStyles.heading, fontSize: '1em' }} {...props} />
          ),
          h5: ({ node, ...props }) => (
            <h5 style={{ ...markdownStyles.heading, fontSize: '0.9em' }} {...props} />
          ),
          h6: ({ node, ...props }) => (
            <h6 style={{ ...markdownStyles.heading, fontSize: '0.85em' }} {...props} />
          ),

          // Linha horizontal
          hr: ({ node, ...props }) => (
            <hr
              style={{
                border: 'none',
                borderTop: `1px solid ${isUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                margin: '1em 0',
              }}
              {...props}
            />
          ),

          // Tabelas (GFM)
          table: ({ node, ...props }) => (
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                margin: '0.5em 0',
                fontSize: '0.9em',
              }}
              {...props}
            />
          ),
          th: ({ node, ...props }) => (
            <th
              style={{
                border: `1px solid ${isUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                padding: '0.5em',
                fontWeight: 600,
              }}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              style={{
                border: `1px solid ${isUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                padding: '0.5em',
              }}
              {...props}
            />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};
